import { zodResolver } from '@hookform/resolvers/zod'
import { HandArrowDownIcon } from '@phosphor-icons/react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useBulkCheckoutPackages } from '@/api/packages/bulk-checkout-packages'
import { TextAreaFormField } from '@/components/form/text-area-form-field'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Typography } from '@/components/ui/typography'
import { toastFactory } from '@/factories'
import { PackageModel } from '@/models'
import { makeBulkCheckoutSchema } from '@/pages/packages/bulk-checkout-modal/bulk-checkout-modal.constants'
import { BulkCheckoutFormData } from '@/pages/packages/bulk-checkout-modal/bulk-checkout-modal.constants.types'
import { getApiErrorMessage } from '@/utils'

import * as S from './bulk-checkout-modal.styled'

export interface BulkCheckoutModalProps {
  onClose: () => void
  packages: PackageModel[]
}

export const BulkCheckoutModal = ({ onClose, packages }: BulkCheckoutModalProps) => {
  const { t } = useTranslation(['packages'])
  const { mutateAsync: bulkCheckout } = useBulkCheckoutPackages()
  const count = packages.length

  const form = useForm<BulkCheckoutFormData>({
    defaultValues: {
      comment: '',
    },
    mode: 'onTouched',
    resolver: zodResolver(makeBulkCheckoutSchema(t)),
  })

  const handleConfirm = async () => {
    try {
      const data = form.getValues()
      await bulkCheckout({
        comment: data.comment || undefined,
        ids: packages.map((pkg) => pkg.id),
      })
      toastFactory.success(t('packages:bulkCheckoutModal.toastSuccess', { count }))
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  return (
    <ConfirmationPopup
      autoWidth
      title={t('packages:bulkCheckoutModal.title', { count })}
      variant="info"
      confirmText={t('packages:bulkCheckoutModal.confirm')}
      cancelText={t('packages:bulkCheckoutModal.cancel')}
      confirmIcon={HandArrowDownIcon}
      onClose={onClose}
      onConfirm={handleConfirm}
    >
      <FormProvider {...form}>
        <S.ModalContent>
          <Typography size="text-huge" color="black700">
            {t('packages:bulkCheckoutModal.body')}
          </Typography>
          <S.CommentSection>
            <TextAreaFormField
              name="comment"
              label={t('packages:bulkCheckoutModal.comment.label')}
              size="small"
              rows={3}
            />
          </S.CommentSection>
        </S.ModalContent>
      </FormProvider>
    </ConfirmationPopup>
  )
}
